import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import { format, parseISO } from "date-fns";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DownloadExcelButton from "components/ReportUI/Downloadexcelbutton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DistanceReportsTable from "./odometerDistanceReporttable";
import DateRangePicker from "layouts/DateTimerange/dateTimeRange";
import DhundhooLoader from "../Loader/Loader";

function OdometerDistanceReport() {
  const [distanceData, setDistanceData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [loading, setLoading] = useState(false);

  const getTodayDateString = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const todayStr = getTodayDateString();
  const defaultStart = `${todayStr}T00:00`;
  const defaultEnd = `${todayStr}T23:59`;

  // ✅ Set to null initially to show placeholder
  const [startTime, setStartTime] = useState(defaultStart);
  const [endTime, setEndTime] = useState(defaultEnd);

  // Snackbar handlers
  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  // Columns for DataTable (Only thingId and distance)
  const columns = [
    { Header: "Vehicle Number", accessor: "thingId", width: "50%" },
    { Header: "Total Distance (Km)", accessor: "distance", width: "50%" },
  ];

  const fetchLogs = async () => {
    // ✅ Validation: Dates must be selected
    if (!startTime || !endTime) {
      setErrorMessage("Please select a date range first.");
      openErrorSB();
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const token = localStorage.getItem("token");

      // Date formatting for the new API (YYYY-MM-DD HH:mm:ss)
      const formatApiDate = (dateString, isEnd) => {
        const dateObj = parseISO(dateString);
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const day = String(dateObj.getDate()).padStart(2, "0");
        const time = isEnd ? "23:59:00" : "00:00:00";
        return `${year}-${month}-${day} ${time}`;
      };

      const payload = {
        fromDateTime: formatApiDate(startTime, false),
        toDateTime: formatApiDate(endTime, true),
      };

      const config = {
        method: "post",
        url: "https://v3.dhundhoo.com/api/odometer/distance",
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
        data: payload,
      };

      const response = await axios.request(config);

      // Mapping response: converting meters to KM and rounding
      const formattedRows = response.data.map((log) => ({
        thingId: log.thingId,
        distance: log.distanceInMeters
          ? (Number(log.distanceInMeters) / 1000).toFixed(1)
          : "0.00",
      }));

      setDistanceData(formattedRows);
      openSuccessSB();
    } catch (error) {
      console.error("API Error:", error);
      setErrorMessage("Error loading Distance report");
      openErrorSB();
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (distanceData.length === 0) {
      setErrorMessage("No data to export");
      openErrorSB();
      return;
    }
    const headers = ["Vehicle Number", "Total Distance (Km)"];
    const rows = distanceData.map((row) => [row.thingId, row.distance].join(","));
    const csvContent = [headers.join(","), ...rows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `OdometerDistanceReport_${format(new Date(), "yyyy-MM-dd")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClear = () => {
    setStartTime(defaultStart); // ✅ Reset back to today
    setEndTime(defaultEnd);
    setDistanceData([]);
    setErrorMessage("");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} pb={0}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid container spacing={2} alignItems="center">

                    {/* ✅ Custom DateRangePicker */}
                    <Grid item xs={12} md={6}>
                      <div style={{ width: '46%' }}>
                        <DateRangePicker
                          startTime={startTime}
                          endTime={endTime}
                          onChange={({ startTime: s, endTime: e }) => {
                            setStartTime(s);
                            setEndTime(e);
                          }}
                          disabled={loading}
                          style={{ Height: '10px' }}
                        />
                      </div>
                    </Grid>

                    <Grid item xs={4} sm={2} md={1.5}>
                      <MDButton
                        fullWidth
                        variant="gradient"
                        color="warning"
                        size="small"
                        onClick={fetchLogs}
                        disabled={loading}
                      >
                        {loading ? <CircularProgress size={20} color="inherit" /> : "LOAD"}
                      </MDButton>
                    </Grid>
                    <Grid item xs={4} sm={2} md={1.5}>
                      <MDButton
                        fullWidth
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleClear}
                        disabled={loading}
                      >
                        CLEAR
                      </MDButton>
                    </Grid>
                    <Grid item xs={4} sm={2} md={1.5}>
                      <DownloadExcelButton fullWidth label="CSV" onClick={exportToCSV} disabled={loading || distanceData.length === 0} />
                    </Grid>
                  </Grid>
                </LocalizationProvider>
              </MDBox>

              {errorMessage && (
                <MDBox px={2} pt={2}>
                  <Alert severity="error">{errorMessage}</Alert>
                </MDBox>
              )}

              <MDBox pt={3} display="flex" justifyContent="center">
                {loading ? (
                  <DhundhooLoader />
                ) : (
                  <DistanceReportsTable
                    table={{ columns, rows: distanceData }}
                    isSorted={true}
                    entriesPerPage={true}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <MDSnackbar
        color="success"
        icon="check"
        title="Odometer Distance Report"
        content="Data Loaded Successfully"
        open={successSB}
        onClose={closeSuccessSB}
        close={closeSuccessSB}
        bgWhite
      />
      <MDSnackbar
        color="error"
        icon="warning"
        title="Odometer Distance Report"
        content="Failed to load logs. Please try again."
        open={errorSB}
        onClose={closeErrorSB}
        close={closeErrorSB}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default OdometerDistanceReport;